import type { PostHogIds } from '../../../lib/telemetry/posthog/types';
export type { CalendarBookingRecord } from '../../../lib/integrations/calendar/types';

/**
 * Type definitions for Live Session Worker
 */

export interface Env {
  /**
   * Cloudflare-owned metadata for the exact Worker version serving this
   * request. Staging deployments tag the version with the full git SHA.
   */
  CF_VERSION_METADATA?: {
    id: string;
    tag: string;
    timestamp: string;
  };
  GEMINI_API_KEY: string;
  /** Optional until the inactive OpenAI candidate reaches the staging parity gate. */
  OPENAI_API_KEY?: string;
  SUPABASE_URL: string;
  SUPABASE_SERVICE_ROLE_KEY: string;
  /**
   * Dedicated inter-service auth secret sent to the gcp-proxy worker and the
   * report-generation endpoint. Preferred over the service-role key when
   * configured; optional during the rollout window (audit A20).
   */
  INTERNAL_AUTH_KEY?: string;
  POSTHOG_API_KEY?: string;
  POSTHOG_HOST?: string;
  /** Laminar LLM observability API key (optional) */
  LMNR_PROJECT_API_KEY?: string;
  /** Staging-only opt-in for digest-level realtime fragment diagnostics. */
  LMNR_REALTIME_DIAGNOSTICS?: string;
  /** Environment label for telemetry (staging/production) */
  ENVIRONMENT?: string;
  /** Pages project URL for internal API calls (e.g. report generation on completion) */
  PAGES_URL?: string;
  LIVE_SESSION: DurableObjectNamespace;
  VIDEO_BUCKET: R2Bucket;
  /** Cloudflare Queue for event-driven video processing (optional for backward compatibility) */
  VIDEO_QUEUE?: Queue;
  /** GCP Proxy Worker URL for metering/research calls */
  GCP_PROXY_URL?: string;
  /** Google integration OAuth client id (integrations hub token refresh). */
  OAUTH_GOOGLE_CLIENT_ID?: string;
  /** Google integration OAuth client secret. */
  OAUTH_GOOGLE_CLIENT_SECRET?: string;
  /** Base64 32-byte AES-256-GCM key for integration token ciphertext. */
  INTEGRATIONS_TOKEN_ENC_KEY?: string;
  /**
   * Staging seam (issue #238) for isolating googleSearch grounding as a stall
   * trigger. Only the exact string 'false' disables grounding; anything else,
   * including unset, keeps it enabled (today's behavior).
   */
  GEMINI_GROUNDING_ENABLED?: string;
  /** Response-latched progressive conversation rollout mode. */
  CONVERSATION_ENGINE_MODE?: string;
}

/**
 * One voice or tone trait. `example` is optional but strongly preferred:
 * concrete brand phrases give the model something to mimic instead of
 * paraphrasing the trait label back at the guest.
 */
export interface BrandVoiceAttribute {
  trait: string;
  example?: string;
}

/**
 * Style guide subset injected into the live-session system prompt. Each
 * field is optional so a partially-populated guide still threads through
 * (the prompt builder renders only the lines that are present).
 */
export interface BrandStyleGuide {
  formality?: 'casual' | 'neutral' | 'formal';
  sentence_length?: 'short' | 'medium' | 'long';
  jargon_level?: 'plain' | 'industry' | 'technical';
  do_use?: string[];
  avoid?: string[];
}

/** Interview context sent from frontend (non-secret, public data only) */
export interface InterviewContext {
  targetName: string;
  targetUrl?: string;
  goal: string;
  details: string;
  productResearch?: {
    summary: string;
    keyFeatures?: string[];
    targetAudience?: string;
    valueProposition?: string;
  };
  /**
   * Exact-case brand name as the brand uses it (e.g. "holito"). Threaded
   * server-side from the workspace row at session start. Rendered verbatim
   * in the system prompt so hollie never re-cases the name.
   */
  brandNameCanonical?: string;
  /** Brand voice traits with optional concrete example phrases. */
  voiceAttributes?: BrandVoiceAttribute[];
  /** Brand tone traits with optional concrete example phrases. */
  toneAttributes?: BrandVoiceAttribute[];
  /** Structured brand style guide. */
  styleGuide?: BrandStyleGuide;
  intervieweeName?: string;
  intervieweeJob?: string;
  intervieweeCompany?: string;
  intervieweeLinkedin?: string;
  intervieweeCompanyUrl?: string;
  /** BCP-47 language code for conducting the interview (e.g., 'en-US', 'es-ES') */
  interviewLanguage?: string;
  /** BCP-47 language code the manager wants to receive the transcript in (e.g., 'en-US') */
  outputLanguage?: string;
  /**
   * Whether the client platform supports getDisplayMedia. False on iOS /
   * Android (see services/screenShareService.ts isSupported). Worker ANDs
   * this with the campaign DB flag before declaring screen-share tools to
   * the realtime model, preventing it from calling request_screen_share when the
   * client can't act on it. Absence of the field = old client, assume
   * supported (backwards compat).
   */
  clientSupportsScreenShare?: boolean;
  /**
   * IANA timezone of the respondent's browser
   * (Intl.DateTimeFormat().resolvedOptions().timeZone). Client-truth data like
   * screen-share capability: validated server-side, used to present calendar
   * slots in the respondent's local time. Absent on old clients; the worker
   * falls back to the host's calendar timezone.
   */
  respondentTimezone?: string;
}

/** Conversation turn for storage */
export interface ConversationTurn {
  /** Authoritative ingress provenance for the unified respondent thread. */
  modality?: 'text' | 'control' | 'speech';
  /** Snapshot field associated with a submitted draft or control. */
  fieldId?: string;
  speaker: 'user' | 'ai' | 'system-prompt' | 'system-confirmation' | 'system-quote-saved';
  text: string;
  timestamp: number;
  /**
   * Stable identity minted (crypto.randomUUID) when the turn is created.
   * The canonical turn key everywhere is
   * `turn.turn_id ?? 'ts:' + turn.timestamp`.
   */
  turn_id?: string;
  /** Wall clock (epoch ms) when the turn was finalized. */
  finalized_at?: number;
  /**
   * True when an AI turn was finalized because the user interrupted, meaning
   * the trailing transcript words were never spoken aloud.
   */
  interrupted?: boolean;
  /** Stable provider-neutral assistant response identity when available. */
  itemId?: string;
  /** False when an interrupted assistant turn was not safely user-heard. */
  heard?: boolean;
  /** Confirmed assistant audio playout before an interrupted tail was removed. */
  heardAudioMs?: number;
}

/** Verified quote from interview */
export interface VerifiedQuote {
  text: string;
  timestamp: number;
}

/**
 * Per-block lifecycle record for the in-memory information-block shown-log.
 *
 * One entry per information block, updated in place (not an append-only event
 * log). `shownAt` records that an image was placed on screen; `respondedAt` is
 * the coverage signal, set when the respondent first speaks while that block is
 * the currently-mounted one. Legacy mode uses this in-memory compatibility
 * record for its cover list. Active mode persists the corresponding lifecycle
 * in the versioned conversation-progress document.
 */
export interface InformationBlockShownRecord {
  /** snake_case `field.name` of the information block. */
  fieldName: string;
  /** Epoch ms when the image was last placed on the respondent's screen. */
  shownAt: number;
  /** Epoch ms of the respondent's first utterance after the mount, if any. */
  respondedAt?: number;
}

/**
 * Resolved descriptor-map value for one information block, built once at DO init
 * from the loaded `form_schema`. Keyed by snake_case `fieldName`. Carries
 * everything the synchronous tool handler needs to send a mount with zero await
 * (`imageUrl`, `title`) plus hollie's text surrogate (`caption`) and the cue
 * (`directive`).
 */
export interface InformationBlockDescriptor {
  /** snake_case `field.name`: the descriptor-map key and tool argument. */
  fieldName: string;
  /** Zero-based position in the server-authoritative form schema. */
  schemaPosition: number;
  /** Relative public serve URL the client fetches to render the image. */
  imageUrl: string;
  /** Static rendition used by the respondent's animation pause control. */
  imageStillUrl?: string;
  /** Direct media provider. Omitted and `upload` both mean first-party R2. */
  imageSource?: 'upload' | 'klipy';
  /** Short headline hollie uses to name the image. */
  title: string;
  /** Rich AI visual description: hollie's text surrogate for the image. */
  caption: string;
  /** One-sentence cue that makes hollie call the show tool. */
  directive: string;
}

/** One weekly bookable window in the host's calendar timezone. */
export interface CalendarBookableWindow {
  weekday: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  enabled: boolean;
  /** Wall-clock HH:MM. */
  start: string;
  /** Wall-clock HH:MM. */
  end: string;
}

/**
 * Server-authoritative calendar block configuration, derived at bootstrap from
 * the session row's form_schema snapshot (spec section 2.6). Only present when
 * the schema carries a valid calendar block; the connection referenced by
 * `connectionId` is verified separately before the tools are declared.
 */
export interface CalendarBookingDescriptor {
  /** snake_case `field.name` of the calendar block. */
  fieldName: string;
  /**
   * Zero-based position of the calendar block in the server-authoritative
   * form_schema snapshot. Descriptor derivation always populates it.
   */
  schemaPosition: number;
  /** The org-scoped integration_connections row this block books against. */
  connectionId: string;
  /** The host's user id. */
  hostUserId: string;
  /** Natural-language directive driving hollie's conversational behavior. */
  directive?: string;
  /** Meeting length in minutes. */
  durationMinutes: number;
  /** Free margin required on each side of the booked slot, in minutes. */
  bufferMinutes: number;
  /** Earliest bookable moment, hours from now. */
  minimumNoticeHours: number;
  /** Latest bookable moment, days from now. */
  bookingHorizonDays: number;
  /**
   * Weekly bookable windows in the host's calendar timezone. Always populated
   * by the descriptor derivation: a schema with no usable custom windows
   * defaults to Monday-Friday 09:00-17:00, so the working-hours frame is
   * explicit rather than inherited. Optional on the type only so a hand-built
   * runtime may omit it and let slot computation apply its own default.
   */
  bookableWindows?: CalendarBookableWindow[];
  /**
   * True when the calendar block is the schema's only field. The prompt then
   * swaps the interview frame (form questions, probing, coverage-based
   * ending) for a booking frame, so the model never invents questions to
   * fill an interview that does not exist.
   */
  bookingOnly: boolean;
}

/** Session configuration */
export interface SessionConfig {
  roomId: string;
  passcode: string;
  sessionId?: string;
  interviewDataId?: string;
  interviewContext?: InterviewContext;
  posthog?: PostHogIds;
  /** Enforced interview language from campaign settings (overrides respondent choice) */
  enforceLanguage?: string | null;
  /**
   * Probing depth (1-4), controls how aggressively hollie probes for
   * follow-ups, ends the call, and pulls quotes. NULL/missing in the DB
   * resolves to 3 (Balanced) at the read site.
   */
  probingDepth?: number;
  /**
   * Number of questions in the campaign's form schema. Threaded into the
   * intro_protocol so hollie sets the same time expectation the join screen
   * showed the respondent. Missing/0 means we couldn't read the schema; the
   * prompt builder falls back to a generic "a few minutes" phrase.
   */
  fieldCount?: number;
}

/** Tool call function structure */
export interface FunctionCall {
  id?: string;
  name: string;
  args?: Record<string, unknown>;
}


/** Tool response result */
export interface ToolResult {
  success: boolean;
  error?: string;
  [key: string]: unknown;
}

// ============================================
// Video Recording Types
// ============================================

/** Recorded track kind. Camera + screen go through Cloudflare Stream; audio
 *  is served directly from R2 since Stream rejects audio-only uploads. */
export type SegmentKind = 'camera' | 'screen' | 'audio';

/** Video segment info for multi-segment recordings */
export interface VideoSegment {
  segment_index: number;
  r2_path: string;
  start_ms: number;
  end_ms: number;
  size_bytes: number;
  status: 'uploading' | 'complete' | 'processing' | 'processed' | 'failed';
  mime_type: string;
  /** Recorded track kind. Defaults to 'camera' for backward compatibility. */
  segment_kind?: SegmentKind;
  /** ISO timestamp when processing completed */
  processed_at?: string;
  /** Path to raw file (before deletion) */
  original_r2_path?: string;
  /** Cloudflare Stream video UID (during processing) */
  stream_uid?: string;
  /** Error message if processing failed */
  error_message?: string;
  /** Timestamp when segment failed */
  failed_at?: string;
  /**
   * Client `Date.now()` captured in `MediaRecorder.onstart` and reported over
   * the socket via the `recording_started` message. Removes the upload-open
   * bias from segment start estimates. Absent for old clients.
   */
  client_start_ms?: number;
  /**
   * Worker-estimated client-minus-server clock offset in ms, derived from the
   * ping/pong heartbeat (lowest-RTT sample). Stamped at segment finalize with
   * the best estimate known by then. Absent when no extended pong arrived.
   */
  clock_offset_ms?: number;
}

/** Internal state for R2 multipart upload */
export interface VideoUploadState {
  uploadId: string;
  key: string;
  parts: R2UploadedPart[];
  currentPartNumber: number;
  pendingChunks: Uint8Array[];
  pendingSize: number;
  totalSize: number;
  mimeType: string;
  startMs: number;
}

/** Video heartbeat message from client */
export interface VideoHeartbeat {
  type: 'video_heartbeat';
  chunksRecorded: number;
  lastChunkTime: number;
  timestamp: number;
}

/** Video heartbeat acknowledgment to client */
export interface VideoHeartbeatAck {
  type: 'video_heartbeat_ack';
  serverChunks: number;
  timestamp: number;
  warning?: string;
}

/** Binary message type identifiers */
export const MESSAGE_TYPES = {
  AUDIO: 0x01,
  VIDEO: 0x02,
  /** Single JPEG frame sampled from the user's shared screen for the selected adapter. */
  SCREEN_FRAME: 0x03,
  /** WebM/MP4 chunk from the user's shared screen, uploaded to R2 for playback. */
  SCREEN_VIDEO: 0x04,
} as const;

// ============================================
// Client Message Types (WebSocket Protocol)
// ============================================

/** Authentication message sent as first WS message (SEC-5: passcode not in URL) */
export interface ClientAuthMessage {
  type: 'auth';
  /** Response-scoped secret, never placed in a URL. */
  response_token?: string;
  /** Explicit media joining does not define the response collection lifecycle. */
  participation_mode?: 'call';
  /** Legacy auth path. Required unless interview_data_id is provided. */
  passcode?: string;
  /**
   * Preferred auth path: the interview_data_id proves authorization because
   * it was created after passcode verification. When present and non-empty,
   * it takes precedence over the passcode.
   */
  interview_data_id?: string;
  /**
   * Enforced interview language from campaign settings (sent with auth for
   * consistency). The worker IGNORES this value and always fetches the
   * server-authoritative value from the DB.
   */
  enforce_language?: string | null;
}

/** Context message sent at session start */
export interface ClientContextMessage {
  type: 'context';
  context: InterviewContext;
  posthog?: PostHogIds;
}

/** Audio data message */
export interface ClientAudioMessage {
  type: 'audio';
  data: string; // base64 encoded PCM audio
}

/** Browser acknowledgment of how much assistant audio was actually played. */
export interface ClientAssistantPlaybackProgressMessage {
  type: 'assistant_playback_progress';
  /** Stable response item identity supplied with realtime_audio. */
  itemId: string;
  /** Provider-neutral content index within the response item. */
  contentIndex: number;
  /** Milliseconds heard by the respondent, used for supported truncation. */
  audioEndMs: number;
}

/** Natural playback of a complete audio source, scoped to its provider lifetime. */
export interface ClientAssistantPlaybackCompletedMessage {
  type: 'assistant_playback_completed';
  itemId: string;
  providerGeneration: number;
  contentIndex: number;
  audioEndMs: number;
}

/** Text input message */
export interface ClientTextMessage {
  type: 'text';
  text: string;
}

/**
 * Heartbeat pong response. A bare `{ type: 'pong' }` from an old client stays
 * valid; new clients additionally echo the ping's timestamp and their own
 * Date.now() so the worker can estimate the client-minus-server clock offset.
 */
export interface ClientPongMessage {
  type: 'pong';
  /** The `timestamp` field of the ping this pong answers (worker clock). */
  echo_timestamp?: number;
  /** Client `Date.now()` (epoch ms) captured when the pong was sent. */
  client_time_ms?: number;
}

/**
 * Client notifies the worker that a MediaRecorder started (its `onstart`
 * fired), carrying the capture-time anchor for the segment about to be
 * uploaded. Sent for every recorder kind (camera, screen, audio) and usually
 * arrives before that segment's first media chunk.
 */
export interface ClientRecordingStartedMessage {
  type: 'recording_started';
  /** Which recorder started: 'camera', 'screen', or 'audio'. */
  kind: SegmentKind;
  /** Client `Date.now()` (epoch ms) captured in MediaRecorder.onstart. */
  client_start_ms: number;
}

/** End session request */
export interface ClientEndMessage {
  type: 'end';
}

/** Confirm interview ending */
export interface ClientConfirmEndingMessage {
  type: 'confirm_ending';
}

/** Cancel interview ending */
export interface ClientCancelEndingMessage {
  type: 'cancel_ending';
}

/** Cancel the inactivity warning (user clicked "I'm still here") */
export interface ClientCancelInactivityMessage {
  type: 'cancel_inactivity';
}

/** Nudge AI to continue */
export interface ClientNudgeMessage {
  type: 'nudge_ai';
}

/** Report an issue */
export interface ClientReportIssueMessage {
  type: 'report_issue';
  description: string;
}

/** Video heartbeat message */
export interface ClientVideoHeartbeatMessage {
  type: 'video_heartbeat';
  chunksRecorded: number;
  lastChunkTime: number;
  timestamp: number;
}

/**
 * Client notifies worker that the browser's screen picker has been opened
 * (`getDisplayMedia` called). Sharing is NOT live yet, the user still has to
 * pick a window/screen. Used to send an anti-hallucination counter-signal to
 * the realtime model, because it otherwise believes the user's verbal "I'm sharing now"
 * and narrates visuals that don't exist.
 */
export interface ClientScreenSharePickerOpenedMessage {
  type: 'screen_share_picker_opened';
  /** 'ai_initiated' (accepted a request) or 'user_initiated' (clicked share button). */
  source: 'ai_initiated' | 'user_initiated';
}

/** Client notifies worker that screen sharing has started (after user consent). */
export interface ClientScreenShareStartedMessage {
  type: 'screen_share_started';
  /** 'ai_initiated' (accepted a request) or 'user_initiated' (clicked share button). */
  source: 'ai_initiated' | 'user_initiated';
}

/** Client notifies worker that screen sharing has stopped. */
export interface ClientScreenShareStoppedMessage {
  type: 'screen_share_stopped';
  reason: 'user_stopped' | 'ai_ended' | 'duration_cap' | 'browser_stopped' | 'error' | 'session_ending';
}

/** Client notifies worker that the user declined an AI-initiated screen share request. */
export interface ClientScreenShareDeclinedMessage {
  type: 'screen_share_declined';
}

/** Client notifies worker that screen sharing failed (e.g. getDisplayMedia rejected). */
export interface ClientScreenShareErrorMessage {
  type: 'screen_share_error';
  message: string;
}

/** Union type for all client messages */
export type ClientMessage =
  | { type: 'conversation_event'; event: import('../../../lib/multimodal-contract').ConversationRequest }
  | { type: 'call_leave' }
  | ClientAuthMessage
  | ClientContextMessage
  | ClientAudioMessage
  | ClientAssistantPlaybackProgressMessage
  | ClientAssistantPlaybackCompletedMessage
  | ClientTextMessage
  | ClientPongMessage
  | ClientRecordingStartedMessage
  | ClientEndMessage
  | ClientConfirmEndingMessage
  | ClientCancelEndingMessage
  | ClientCancelInactivityMessage
  | ClientNudgeMessage
  | ClientReportIssueMessage
  | ClientVideoHeartbeatMessage
  | ClientScreenSharePickerOpenedMessage
  | ClientScreenShareStartedMessage
  | ClientScreenShareStoppedMessage
  | ClientScreenShareDeclinedMessage
  | ClientScreenShareErrorMessage;

/** Type guard for client message types */
export function isClientMessage(data: unknown): data is ClientMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    typeof (data as { type: unknown }).type === 'string'
  );
}

// ============================================
// Server Message Types (to Client)
// ============================================

/** Ready message after connection */
export interface ServerReadyMessage {
  type: 'ready';
  message: string;
}

/** Ping for heartbeat */
export interface ServerPingMessage {
  type: 'ping';
  timestamp: number;
}

/** Connection established with the selected realtime model. */
export interface ServerConnectedMessage {
  type: 'connected';
  message: string;
}

/** Reconnected after disconnect */
export interface ServerReconnectedMessage {
  type: 'reconnected';
  message: string;
  /** Whether the provider session survived or was recreated from context. */
  upstreamSession: 'preserved' | 'recreated';
  /** Reconnect mic-gate rule preserving the issue #238 self-hearing fix. */
  microphoneGate: 'open_immediately' | 'wait_for_assistant_output';
}

/** Reconnecting status */
export interface ServerReconnectingMessage {
  type: 'reconnecting';
  attempt: number;
  maxAttempts: number;
  message: string;
}

/** Session refreshed for continuity */
export interface ServerSessionRefreshedMessage {
  type: 'session_refreshed';
  message: string;
}

/** Stable public error categories. Internal causes never cross the socket. */
export type ServerErrorCode =
  | 'conversation_unavailable'
  | 'service_unavailable'
  | 'realtime_unavailable';

/** Provider-neutral error outcome for the respondent client. */
export interface ServerErrorMessage {
  type: 'error';
  code: ServerErrorCode;
  terminal: boolean;
  retryable: boolean;
}

/** Inactivity warning */
export interface ServerInactivityWarningMessage {
  type: 'inactivity_warning';
  countdown: number;
  message: string;
}

/** Inactivity canceled */
export interface ServerInactivityCanceledMessage {
  type: 'inactivity_canceled';
}

/** User kicked for inactivity */
export interface ServerKickedMessage {
  type: 'kicked';
  reason: string;
}

/** Session duration warning */
export interface ServerSessionDurationWarningMessage {
  type: 'session_duration_warning';
  remainingSeconds: number;
  message: string;
}

/** Interview ending countdown */
export interface ServerInterviewEndingMessage {
  type: 'interview_ending';
  countdown: number;
}

/** Interview ended */
export interface ServerInterviewEndedMessage {
  type: 'interview_ended';
}

/** Issue reported acknowledgment */
export interface ServerIssueReportedMessage {
  type: 'issue_reported';
}

/** Disconnected notification */
export interface ServerDisconnectedMessage {
  type: 'disconnected';
}

/** Provider-neutral identity of the server-authoritative model latched for a session. */
export interface ServerRealtimeModelProfile {
  /** Stable holito routing key selected and latched by the server. */
  key: string;
  /** Operator-facing profile label. */
  label: string;
  /** Provider family behind the profile. */
  provider: string;
  /** Concrete upstream model identifier used by the selected adapter. */
  upstreamModelId: string;
}

/** Negotiated media policy and authoritative audit metadata for the selected realtime model. */
export interface ServerRealtimeSessionReadyMessage extends ServerRealtimeCorrelation {
  type: 'realtime_session_ready';
  microphoneSampleRateHz: number;
  preferredChunkDurationMs: number;
  /** Full git SHA attached to the serving Worker version by staging CI. */
  deploymentRevision: string;
  /**
   * Server-latched profile, used by manual release audits to confirm routing.
   * Present only when the server-decided staging diagnostics gate is on, so
   * respondent browsers never learn the provider or model.
   */
  modelProfile?: ServerRealtimeModelProfile;
  /** Canonical tool names actually declared to the selected realtime adapter. */
  declaredTools: string[];
}

/** Incremental provider-neutral transcript event. */
export interface ServerRealtimeCorrelation {
  clientConnectionGeneration?: number;
  providerGeneration?: number;
  responseGeneration?: number;
  logicalTurnId?: string;
  logicalTurnNumber?: number;
  providerEventId?: string;
  providerEventIdSource?: 'upstream' | 'synthetic' | 'absent';
  localEventSequence?: number;
  itemIdSource?: 'upstream' | 'synthetic';
}

/** Incremental provider-neutral transcript event. */
export interface ServerRealtimeTranscriptMessage extends ServerRealtimeCorrelation {
  type: 'realtime_transcript';
  speaker: 'user' | 'ai';
  text: string;
  final: boolean;
  itemId?: string;
  transcriptFragmentSequence?: number;
}

/** Provider-neutral assistant PCM audio frame. */
export interface ServerRealtimeAudioMessage extends ServerRealtimeCorrelation {
  type: 'realtime_audio';
  data: string;
  sampleRateHz: number;
  itemId: string;
  contentIndex: number;
  audioFragmentSequence?: number;
}

/** Assistant response lifecycle state used by playback and mic protection. */
export interface ServerRealtimeResponseStateMessage extends ServerRealtimeCorrelation {
  type: 'realtime_response_state';
  state: 'started' | 'completed';
  itemId?: string;
}

/** Assistant output was interrupted and pending browser playback must stop. */
export interface ServerRealtimeInterruptedMessage extends ServerRealtimeCorrelation {
  type: 'realtime_interrupted';
  itemId?: string;
}

/** Provider-neutral tool lifecycle evidence used by product behavior and manual audits. */
export interface ServerRealtimeToolStateMessage {
  type: 'realtime_tool_state';
  callId: string;
  name: string;
  state: 'called' | 'completed' | 'failed';
  /** Worker wall-clock timestamp in epoch milliseconds. */
  timestamp: number;
  /** Provider-neutral assistant response that emitted this tool call. */
  responseItemId?: string;
  /** Canonical arguments with respondent identity values redacted. */
  arguments?: Record<string, unknown>;
  /** Safe, allowlisted result evidence on terminal lifecycle messages. */
  result?: Record<string, unknown>;
}

/** Worker asks the client to prompt the user for screen-share consent. */
export interface ServerScreenShareRequestedMessage {
  type: 'screen_share_requested';
  reason: string;
}

/** Worker asks the client to stop the active screen share (AI reached comprehension or cap hit). */
export interface ServerScreenShareEndRequestedMessage {
  type: 'screen_share_end_requested';
  reason: string;
}

/**
 * Acknowledgment of a client video heartbeat, echoing the server-side chunk
 * count so the client can detect drift. Shares its shape with
 * {@link VideoHeartbeatAck}; declared here as a discriminated member so it can
 * join {@link ServerMessage}.
 */
export interface ServerVideoHeartbeatAckMessage {
  type: 'video_heartbeat_ack';
  serverChunks: number;
  timestamp: number;
  warning?: string;
}

/**
 * Tells a SURVIVING continuous client recorder to stop and restart its
 * MediaRecorder so the next segment opens on a header-bearing chunk 0. Emitted
 * only on a FULL server re-init (the worker rebuilt the VideoHandler) when a
 * prior recording was active. Not sent on the first connect or on a grace-
 * resume (the same VideoHandler keeps the in-flight segment). A page reload
 * destroys the recorder entirely, so a fresh recorder already starts at chunk 0
 * and this message is harmlessly ignored when no recorder survives.
 */
export interface ServerRestartRecordingMessage {
  type: 'restart_recording';
  /** The recording kind the worker is tracking ('camera' or 'audio'). */
  kind: SegmentKind;
}

/**
 * Worker tells the client to mount an information-block image on the
 * respondent's screen. Fired by the show_information_block tool and by the
 * best-effort reconnect re-mount. The wire name is `mount_` (not the tool name
 * `show_information_block`) so the two are never confused on the wire. Carries
 * imageUrl (the client fetches it) and an optional title for labelling; the
 * caption is the worker's server-side surrogate and is never sent to the client.
 */
export interface ServerMountInformationBlockMessage {
  type: 'mount_information_block';
  fieldName: string;
  imageUrl: string;
  title?: string;
  imageStillUrl?: string;
  imageSource?: 'upload' | 'klipy';
}

/**
 * Worker tells the client to unmount the information-block image. Fired only by
 * the scoped interrupt of the mount-triggering turn (section 6.4).
 */
export interface ServerUnmountInformationBlockMessage {
  type: 'unmount_information_block';
  fieldName: string;
}

/**
 * Discriminated union of every message the worker sends to the client. This is
 * the single source of truth for the WS protocol: the call-screen client
 * re-exports this union (see components/call-screen/protocol-types.ts) so a
 * server-only addition or removal forces a corresponding client-side change.
 *
 * Membership is asserted at runtime by the protocol-conformance test against
 * the `case` labels the client's message-router switches on, so a type the
 * worker stops sending (or one the client stops handling) fails the build.
 */
export type ServerMessage =
  | ServerReadyMessage
  | ServerPingMessage
  | ServerConnectedMessage
  | ServerReconnectedMessage
  | ServerReconnectingMessage
  | ServerSessionRefreshedMessage
  | ServerSessionDurationWarningMessage
  | ServerErrorMessage
  | ServerInactivityWarningMessage
  | ServerInactivityCanceledMessage
  | ServerKickedMessage
  | ServerInterviewEndingMessage
  | ServerInterviewEndedMessage
  | ServerIssueReportedMessage
  | ServerDisconnectedMessage
  | ServerRealtimeSessionReadyMessage
  | ServerRealtimeTranscriptMessage
  | ServerRealtimeAudioMessage
  | ServerRealtimeResponseStateMessage
  | ServerRealtimeInterruptedMessage
  | ServerRealtimeToolStateMessage
  | ServerVideoHeartbeatAckMessage
  | ServerScreenShareRequestedMessage
  | ServerScreenShareEndRequestedMessage
  | ServerRestartRecordingMessage
  | ServerMountInformationBlockMessage
  | ServerUnmountInformationBlockMessage;

/**
 * WebSocket close codes the worker uses to terminate a client connection for an
 * unrecoverable reason. The client classifies exactly these as fatal (no
 * reconnect). 1008 is intentionally NOT a member: the worker never sends it,
 * and the client carried a dead 1008 branch until A14 removed it. Kept as the
 * shared close-code table so the conformance test can pin the client's
 * classification against what the worker actually emits.
 *
 * - 4001: invalid credentials / auth required / auth timeout.
 * - 4002: conversation unavailable for a non-retryable private reason.
 * - 4003: retryable upstream service unavailable.
 * - 4004: non-retryable realtime service unavailable.
 *
 * 4000 (old-socket supersession on a duration refresh) and 1000 (clean close)
 * are internal/clean and are not surfaced to the client as fatal.
 */
export const FATAL_CLOSE_CODES = [4001, 4002, 4003, 4004] as const;

/** A close code the worker uses to signal an unrecoverable client error. */
export type FatalCloseCode = (typeof FATAL_CLOSE_CODES)[number];
